/**
 * DriverActionsPanel — magic-link self-service for a driver/booker viewing
 * /d/:token. Lets them:
 *   • Set / update arrival ETA
 *   • Update their phone (so dispatch can reach them on the day)
 *   • Sign their name confirming they've read the rules
 *
 * All writes go through the SECURITY DEFINER RPC `lbs_driver_update_booking`
 * which whitelists fields and writes an audit row tagged actor_kind='driver'.
 */
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, PenLine } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { AuDateTimeInput } from "@/components/AuDateTime";
import { composeInSiteTz, decomposeInSiteTz } from "@/lib/datetime";

// ISO → "YYYY-MM-DDTHH:MM" in SITE time (the ETA is entered in site time).
function toLocalDtInput(iso, tz) {
  if (!iso || isNaN(new Date(iso).getTime())) return "";
  const p = decomposeInSiteTz(iso, tz);
  return `${p.dateStr}T${p.timeStr}`;
}

function fromLocalDtInput(v, tz) {
  const [d, t] = v.split("T");
  const [year, month, day] = d.split("-").map(Number);
  const [hour, minute] = (t || "00:00").split(":").map(Number);
  return composeInSiteTz({ year, month, day, hour, minute }, tz);
}

export default function DriverActionsPanel({ token, booking, onUpdated, tz = "Australia/Melbourne", tzLabel }) {
  const [eta,        setEta]        = useState(toLocalDtInput(booking?.arrival_eta_at, tz));
  const [phone,      setPhone]      = useState(booking?.driver_phone || "");
  const [signedName, setSignedName] = useState(booking?.driver_signed_name || "");
  const [rulesRead,  setRulesRead]  = useState(!!booking?.site_rules_accepted_at);
  const [saving,     setSaving]     = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        // datetime-local has no zone — let the browser localise back to ISO.
        arrival_eta_at:        eta ? fromLocalDtInput(eta, tz) : null,
        driver_phone:          phone.trim() || null,
        driver_signed_name:    signedName.trim() || null,
        site_rules_accepted:   rulesRead,
      };
      const { data, error } = await supabase.rpc("lbs_driver_update_booking", {
        p_token:   token,
        p_payload: payload,
      });
      if (error) throw error;
      toast.success("Saved. Dispatch can now see your update.");
      onUpdated?.(data);
    } catch (e) {
      toast.error(e?.message || "Couldn't save your update");
    } finally {
      setSaving(false);
    }
  };

  const alreadySigned = !!booking?.site_rules_accepted_at && !!booking?.driver_signed_name;
  const closed = ["completed", "cancelled", "rejected", "no_show"].includes(booking?.status);

  return (
    <Card data-testid="driver-actions-panel">
      <CardContent className="py-6 space-y-4">
        <div className="flex items-start gap-2">
          <PenLine className="h-4 w-4 mt-1 text-blue-600" />
          <div>
            <h2 className="text-lg font-semibold">Your booking</h2>
            <p className="text-xs text-slate-500">
              Update your ETA, contact number and confirm you&apos;ve read the site rules.
              Dispatch will see these changes immediately — no login required.
            </p>
          </div>
        </div>

        {closed && (
          <div className="rounded-md bg-slate-100 px-3 py-2 text-xs font-medium text-slate-700" data-testid="driver-actions-closed">
            This booking is {booking.status.replace("_", " ")}, so it can no longer be updated.
          </div>
        )}
        <fieldset disabled={closed} className="space-y-4 disabled:opacity-60" data-testid="driver-actions-fieldset">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Arrival ETA{tzLabel ? <span className="normal-case tracking-normal text-slate-400"> ({tzLabel})</span> : null}
            </Label>
            <AuDateTimeInput
              value={eta}
              disabled={closed}
              onChange={(e) => setEta(e.target.value)}
              data-testid="driver-eta-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              My phone (on the day)
            </Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0411 …"
              data-testid="driver-phone-input"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate-500">
            Sign your name
          </Label>
          <Input
            value={signedName}
            onChange={(e) => setSignedName(e.target.value)}
            placeholder="First Last"
            data-testid="driver-signature-input"
          />
        </div>

        <label className="flex items-center gap-2 text-sm rounded-md border bg-slate-50 px-3 py-2">
          <Checkbox
            checked={rulesRead}
            onCheckedChange={(v) => setRulesRead(!!v)}
            data-testid="driver-rules-read-checkbox"
          />
          <span>
            I&apos;ve read the site rules &amp; map below and agree to follow them on arrival.
          </span>
        </label>

        {alreadySigned && (
          <div
            className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5"
            data-testid="driver-already-signed"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Signed by <strong>{booking.driver_signed_name}</strong> on{" "}
            {new Date(booking.site_rules_accepted_at).toLocaleString("en-AU", {
              timeZone: tz, day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
            })}{tzLabel ? ` (${tzLabel})` : ""}
          </div>
        )}

        <div className="flex justify-end">
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="driver-actions-save"
          >
            {saving ? "Saving…" : "Save my update"}
          </Button>
        </div>
        </fieldset>
      </CardContent>
    </Card>
  );
}
